'use client'

import { useSignal, useSignals } from '@preact/signals-react/runtime'
import ClientBookingToolWrapper from '@susu/headless-commerce/components/ClientBookingToolWrapper/ClientBookingToolWrapper'
import ClientSideSlider from '@susu/headless-commerce/components/SideSlider/ClientSideSlider'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import { useClientNav } from '@susu/headless-commerce/features/navigation/components/ClientNavProvider'
import { NavSubmenu } from '@susu/headless-commerce/features/navigation/components/NavSubmenu'
import type {
  NavigationFontStyling,
  NavigationItem,
} from '@susu/headless-commerce/features/navigation/domain'
import type { NavigationLink } from '@susu/headless-commerce/gql/generated/graphql'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { generateUrlFromLinkContent } from '@susu/headless-commerce/utils/UrlGenerator'
import { enableScroll } from '@susu/headless-commerce/utils/scrollUtils'
import { URLData } from '@susu/headless-commerce/utils/tracking/segment'
import { trackNavLinkClick } from '@susu/headless-commerce/utils/tracking/trackNavLinkClick'
import { isNotUndefined } from '@susu/undefined'
import classNames from 'classnames'
import { useCallback } from 'react'
import styles from './ClientNavLink.module.scss'

export type ClientNavLinkProps = {
  className?: string
  fontStyling?: NavigationFontStyling
  isSubMenu?: boolean
  item: NavigationItem
}

export const ClientNavLink = ({
  className,
  fontStyling = 'regular',
  isSubMenu = false,
  item,
}: ClientNavLinkProps) => {
  useSignals()

  const isBookingToolOpen = useSignal(false)
  const locale = useLocale()
  const country = useCountry()
  const pageType = usePageType()
  const { url, locationId } = URLData()

  const { isSubMenuOpened, openSubMenu, closeSubMenu, closeNav } =
    useClientNav()

  const linkUrl = isNotUndefined(item.link)
    ? generateUrlFromLinkContent(
        item.link as NavigationLink,
        country.siteID,
        locale,
      )
    : undefined

  const linkText = item.text

  const isBookingToolLink = item.link?.linkType === 'Booking Tool'
  const isSubMenuLink =
    isNotUndefined(item.subNavigation) ||
    isNotUndefined(item.subNavigationCards)
  const isRenderedInsideSubMenu = isSubMenu
  const name = item.text

  const handleBookingToolLinkClick = useCallback(() => {
    closeSubMenu()
    closeNav()
    enableScroll()

    const { trackingMenuIdValue, trackingParentId } = item

    trackNavLinkClick({
      country,
      isSubMenuLink,
      locale,
      locationId,
      pageType,
      url,
      trackingMenuIdValue,
      trackingParentId,
    })

    isBookingToolOpen.value = true
  }, [
    closeNav,
    closeSubMenu,
    isBookingToolOpen,
    item,
    country,
    locale,
    locationId,
    pageType,
    url,
    isSubMenuLink,
  ])

  const handleBookingToolClose = useCallback(() => {
    isBookingToolOpen.value = false
  }, [isBookingToolOpen])

  const handleLinkClick = useCallback(() => {
    closeSubMenu()
    closeNav()
    enableScroll()

    const { trackingMenuIdValue, trackingParentId } = item

    trackNavLinkClick({
      country,
      isSubMenuLink,
      locale,
      locationId,
      pageType,
      trackingMenuIdValue,
      trackingParentId,
      url,
    })
  }, [
    closeNav,
    closeSubMenu,
    country,
    isSubMenuLink,
    item,
    locale,
    locationId,
    pageType,
    url,
  ])

  const handleButtonClick = useCallback(() => {
    if (isSubMenuOpened.value[name] === true) {
      closeSubMenu()
    } else {
      openSubMenu(name)
    }

    const { trackingMenuIdValue, trackingParentId } = item

    trackNavLinkClick({
      country,
      isSubMenuLink,
      locale,
      locationId,
      pageType,
      url,
      trackingMenuIdValue,
      trackingParentId,
    })
  }, [
    closeSubMenu,
    country,
    isSubMenuLink,
    isSubMenuOpened.value,
    locale,
    locationId,
    name,
    openSubMenu,
    pageType,
    url,
    item,
  ])
  const linkClass = classNames(
    {
      [`${styles['nav__link']} nav__link`]: !isRenderedInsideSubMenu,
      [`${styles['nav__link--active']} nav__link--active`]:
        isSubMenuOpened.value[name] === true,
      [styles['nav__submenu-link']]: isRenderedInsideSubMenu,
      [styles['nav__link--small']]: fontStyling === 'small',
    },
    className,
  )

  if (isSubMenuLink === false) {
    if (isBookingToolLink) {
      return (
        <>
          <button
            className={linkClass}
            onClick={handleBookingToolLinkClick}
            type="button"
            data-testid={item.link?.automationId}
          >
            {linkText}
          </button>
          <ClientSideSlider
            isOpen={isBookingToolOpen.value}
            onClose={handleBookingToolClose}
            fullScreen="mobile"
            slideInFrom="right"
          >
            <ClientBookingToolWrapper
              source={'navigation'}
              bookingToolOpen={isBookingToolOpen.value}
              onClose={handleBookingToolClose}
              medium={'menu'}
            />
          </ClientSideSlider>
        </>
      )
    }

    return (
      <a
        className={linkClass}
        href={linkUrl}
        onClick={handleLinkClick}
        data-testid={item.link?.automationId}
      >
        {linkText}
      </a>
    )
  }

  return (
    <>
      <button
        className={linkClass}
        onClick={handleButtonClick}
        type="button"
        data-testid={
          item.subNavigation?.groups?.[0]?.testAutomationAttribute ??
          item.subNavigationCards?.groups?.[0]?.testAutomationAttribute
        }
      >
        {linkText}
      </button>
      {isNotUndefined(item.subNavigation) && (
        <NavSubmenu
          navigation={item.subNavigation}
          open={isSubMenuOpened.value[name] === true}
          locale={locale}
        />
      )}
      {isNotUndefined(item.subNavigationCards) && (
        <NavSubmenu
          navigation={item.subNavigationCards}
          open={isSubMenuOpened.value[name] === true}
          locale={locale}
          isCards
        />
      )}
    </>
  )
}
